import { axios } from '@Api/axiosClient';
import type { MppPolicy } from '@Types/MppPolicy';
import type { ClientRequestDetail } from '@Types/ClientRequestDetail.interface';
import type { FinalizeCollectRequestDto } from '@Types/FinalizeCollectRequest.interface';
import type { SccSubmitResult } from '@Types/SccSubmitResult.interface';
import type { Address } from '@Types/WiringInstrucions.interface';

const baseUrl: string = 'api/RequestFlow';

export const markRequestAsStartedApi = async (): Promise<void> => {
    await axios.post(`${baseUrl}/MarkRequestAsStarted`);
};

export const getInsuranceOptionsApi = async (): Promise<MppPolicy[]> => {
    const response = await axios.get(`${baseUrl}/GetInsuranceOptions`);
    return response.data;
};

export const finalizeSendRequestApi = async (): Promise<void> => {
    await axios.post(`${baseUrl}/FinalizeSendRequest`);
};

export const getClientRequestDetailApi =
    async (): Promise<ClientRequestDetail> => {
        const response = await axios.get(`${baseUrl}/GetClientRequestDetail`);
        return response.data;
    };

export const finalizeConfirmRequestApi = async (
    acceptAccountDetails: boolean,
    rejectionReason: number | null = null,
    rejectionAdditionalDetails: string | null = null,
    personalAddress: Address | null = null,
): Promise<SccSubmitResult> => {
    const response = await axios.post(`${baseUrl}/FinalizeConfirmRequest`, { acceptAccountDetails, rejectionReason, rejectionAdditionalDetails, personalAddress });
    // A BFF that predates PLAT-2857 returns no body — treat as the legacy success outcome.
    return response.data?.action ? response.data : { action: 'success' };
};

export const finalizeCollectRequestApi = async (dto: FinalizeCollectRequestDto): Promise<SccSubmitResult> => {
    const response = await axios.post(`${baseUrl}/FinalizeCollectRequest`, dto);
    // A BFF that predates PLAT-2857 returns no body — treat as the legacy success outcome.
    return response.data?.action ? response.data : { action: 'success' };
};

export const completeSccPlaidSuccessApi = async (publicToken: string): Promise<SccSubmitResult> => {
    const response = await axios.post(`${baseUrl}/CompleteSccPlaidSuccess`, { publicToken });
    return response.data?.action ? response.data : { action: 'success' };
};

export const completeSccPlaidFailureApi = async (
    reason: string,
    exitPayload?: {
        errorType?: string,
        errorCode?: string,
        institutionName?: string,
        status?: string,
        linkSessionId?: string,
    }
): Promise<SccSubmitResult> => {
    const response = await axios.post(`${baseUrl}/CompleteSccPlaidFailure`, { reason, exitPayload });
    return response.data?.action ? response.data : { action: 'success' };
};

export const isPossibleBrokerageDestinationApi = async (
    routingNumber: string,
    accountHolderName: string,
): Promise<{ possibleBrokerage: boolean; primaryBrokerage: string } | null> => {
    const response = await axios.post(`${baseUrl}/IsPossibleBrokerageDestination`, { routingNumber, accountHolderName });
    // Fail closed: a 200 with no boolean possibleBrokerage means the destination was never
    // actually verified. Return null so the composable's fail-fast path blocks Continue,
    // rather than coercing the missing field to false and passing it off as a verified "no".
    if (typeof response.data?.possibleBrokerage !== 'boolean') return null;
    return {
        possibleBrokerage: response.data.possibleBrokerage,
        primaryBrokerage: response.data.primaryBrokerage ?? '',
    };
};
